import { z } from 'zod';
import { DateString, Timestamp, Uuid } from './common.js';
import { AnalysisKind, AnalysisStatus, Sensitivity } from './vocab.js';

/** Re-exported from `vocab.ts`, where the closed vocabularies live. */
export { ANALYSIS_STATUSES, AnalysisStatus } from './vocab.js';

/** A typed input ref on an analysis (`inputs[]`, kernel item 4). */
export const AnalysisInputRef = z.object({
  type: z.enum(['fact', 'claim', 'analysis']),
  id: Uuid,
});
export type AnalysisInputRef = z.infer<typeof AnalysisInputRef>;

/* ————————————————————————— caveats ————————————————————————— */

/**
 * The hedging an analysis would otherwise write into its prose — how the person
 * was matched, which anchors carried it, how thin the record is
 * (docs/notes/2026-08-31-first-prod-runs.md §2; decided 2026-08-31, Soroush).
 *
 * The first production runs spent about half the quick-search summary on it. The
 * information is useful and it is not what a summary is for, so it moves out of
 * the prose and into its own field: the prose's job is the person, scannable,
 * and the dashboard renders these as a collapsed line beside the section rather
 * than inline.
 *
 * The caps are here rather than in a prompt because a prompt asking for "short"
 * is a request and this is the thing the ingest plane can actually check.
 */
export const ANALYSIS_CAVEAT_MAX_CHARS = 300;
export const ANALYSIS_CAVEATS_MAX = 6;

export const AnalysisCaveat = z.string().min(1).max(ANALYSIS_CAVEAT_MAX_CHARS);
export type AnalysisCaveat = z.infer<typeof AnalysisCaveat>;

export const AnalysisCaveats = z.array(AnalysisCaveat).max(ANALYSIS_CAVEATS_MAX);
export type AnalysisCaveats = z.infer<typeof AnalysisCaveats>;

/* ————————————————————————— the typed content keys —————————————————————————
 *
 * **Prose is plain text; structure is a field** (2026-09-01, Soroush —
 * `docs/notes/2026-09-01-research-layout.md`). A model asked for prose writes
 * prose, and a reading the office actually scans as a list — the criteria of a
 * fit read, the three buckets of the conversation notes — is a list on the wire
 * rather than markdown inside a paragraph the record then has to parse back out.
 *
 * The keys below are the ones something downstream reads. Everything else the
 * payload holds still passes through untouched.
 */

/**
 * One row of `club_fit.criteria`. `met` is the record's answer about the value —
 * `true` where the record supports it, `false` where the record shows tension
 * with it — and is **optional**: a row written before the field existed reads as
 * met, which is what the dashboard does with a missing key.
 */
export const AnalysisCriterion = z
  .object({
    criterion: z.string().min(1),
    met: z.boolean().optional(),
    /**
     * Nullable *and* optional on purpose, and this object is written by the
     * pipeline as well as read by the record: absent is a row from before the
     * key existed, `null` is a criterion the reading had nothing to add to.
     */
    note: z.string().nullable().optional(),
  })
  .passthrough();
export type AnalysisCriterion = z.infer<typeof AnalysisCriterion>;

/**
 * The per-list caps of `conversation_notes`' three buckets. They are here, beside
 * the caveat caps and for the same reason: a prompt asking for "at most four,
 * one sentence each" is a request, and this is the thing the ingest plane can
 * actually check. The pipeline restates them rather than importing them
 * (`agent/app/research/agents/summary.py`), so the two must be changed together.
 */
export const CONVERSATION_LIST_MAX = 4;
export const CONVERSATION_ITEM_MAX_CHARS = 240;

/** One of `conversation_notes`' buckets — short single sentences, capped. */
export const ConversationList = z
  .array(z.string().min(1).max(CONVERSATION_ITEM_MAX_CHARS))
  .max(CONVERSATION_LIST_MAX);
export type ConversationList = z.infer<typeof ConversationList>;

/**
 * The two lists of `relationship_read` — this person in relation to the
 * organisation (verticals note §0.8, the business vertical's second kind).
 *
 * `connections` are the concrete links between the two: people they both know,
 * institutions and places they share, the sector they are both in. `angles` are
 * what each side plausibly wants out of knowing the other, and why the
 * introduction is worth taking.
 *
 * **Uncapped on both axes, unlike the conversation-notes buckets** — per item
 * *and* per list — and both halves of that are a choice rather than an
 * oversight. Those buckets are one-sentence prompts read at a glance before a
 * meeting, so 4 × 240 is a real shape and the caps are what enforce it.
 *
 * *Per item*: an angle is a claim with its reason attached, and does not fit in
 * a prompt-length line. What a character cap enforces is a *form*, and this form
 * is different, so restating their number here would have imposed a shape nobody
 * chose.
 *
 * *Per list*: the number of links between a person and an organisation is a
 * fact about them, not a budget — three shared institutions is three and seven
 * is seven. A count cap would **truncate** the reading rather than shorten it,
 * and nothing downstream could tell a person with four connections from one with
 * a dozen. `club_fit.criteria` above is uncapped for the same reason and has
 * been since it shipped. The budget these caps are sometimes reached for is the
 * *prompt's*, and that is protected at the source instead — `org_profile` and
 * `club_values` are capped where the office writes them.
 *
 * The items are still non-empty strings: an empty bullet is a rendering
 * artefact, not a reading.
 */
export const RelationshipList = z.array(z.string().min(1));
export type RelationshipList = z.infer<typeof RelationshipList>;

/**
 * `wealth_band`'s answer: an **estimated** net-worth band for every person, in
 * every vertical (docs/notes/2026-10-08-net-worth-bands.md). Four bands,
 * deliberately coarse, and never shown as anything but an estimate. A record that
 * supports no estimate carries `band: null`.
 */
export const WEALTH_BANDS = ['under_5m', '5m_to_25m', '25m_to_100m', 'over_100m'] as const;
export const WealthBand = z.enum(WEALTH_BANDS);
export type WealthBand = z.infer<typeof WealthBand>;

/** The band in words, the same on the record and on the connector. */
export const WEALTH_BAND_LABELS: Record<WealthBand, string> = {
  under_5m: 'under $5M',
  '5m_to_25m': '$5M to $25M',
  '25m_to_100m': '$25M to $100M',
  over_100m: 'over $100M',
};

/** The analysis kind that carries the band; `summary` is what it rests on. */
export const WEALTH_BAND_KIND = 'wealth_band';

/**
 * An analysis's payload. **Passthrough, and every key optional** — `content` is
 * opaque jsonb server-side, no kind is required to carry any particular key, and
 * a row written before a field existed stays valid. What is pinned is the shape
 * of the keys the record and the brief actually read:
 *
 * - `summary` — the prose every kind may carry, plain text, paragraphs separated
 *   by a blank line.
 * - `criteria` — `club_fit`'s rows.
 * - `openers` / `common_ground` / `carry_lightly` — `conversation_notes`' three
 *   buckets.
 * - `connections` / `angles` — `relationship_read`'s two lists.
 * - `band` — `wealth_band`'s estimated net-worth band.
 * - `caveats` — the hedging the prose no longer carries, pinned once here on the
 *   container every kind shares rather than five times over five shapes.
 */
export const AnalysisContent = z
  .object({
    summary: z.string().optional(),
    criteria: z.array(AnalysisCriterion).optional(),
    openers: ConversationList.optional(),
    common_ground: ConversationList.optional(),
    carry_lightly: ConversationList.optional(),
    /**
     * `relationship_read`: the concrete links between the person and the
     * organisation — people, institutions, places, sector. `summary` above is
     * the same reading in prose.
     */
    connections: RelationshipList.optional(),
    /**
     * `relationship_read`: what each side plausibly wants out of the other, and
     * why the introduction is worth taking.
     */
    angles: RelationshipList.optional(),
    /**
     * `wealth_band`: the estimated band, or `null` where the record does not
     * support an estimate (shown as "No data"). `summary` is what it rests on.
     */
    band: WealthBand.nullable().optional(),
    caveats: AnalysisCaveats.optional(),
  })
  .passthrough();
export type AnalysisContent = z.infer<typeof AnalysisContent>;

/**
 * An analysis as the record and brief render it (server-design.md §7.5).
 * `sensitivity` is the server's computed max over the kind default and the
 * inputs' *current* sensitivity — never a frozen write-time value.
 *
 * Model, prompt and commit provenance (`produced_by`) stays server-side; the
 * dashboard gets the producing run id and nothing else.
 */
export const Analysis = z.object({
  id: Uuid,
  kind: AnalysisKind,
  content: AnalysisContent,
  status: AnalysisStatus,
  sensitivity: Sensitivity,
  /** Oldest as_of-bearing input — "records clear as of Aug 2026". */
  as_of: DateString.nullable(),
  inputs: z.array(AnalysisInputRef),
  run_id: Uuid.nullable(),
  created_at: Timestamp,
});
export type Analysis = z.infer<typeof Analysis>;
